import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown, Share2, Megaphone, Search, Key, Mail, CalendarDays, ImageIcon,
} from 'lucide-react';
import {
  getTasksForProject,
  repurposeArticle, createQuickWinMetaData, createQuickWinAdCopy,
  createQuickWinKeywordResearch, createQuickWinEmailNewsletter,
  createQuickWinContentCalendar, selectImagesForTask, API_BASE_URL,
} from '../services/api';
import ProgressModal from './ProgressModal';
import GenerateConfirmModal from './GenerateConfirmModal';
import './ContentCatalogue.css';

interface CatalogueMenuBarProps {
  projectId: string;
  streamNames: string[];
  onComplete: () => void;
}

const CatalogueMenuBar: React.FC<CatalogueMenuBarProps> = ({ projectId, streamNames, onComplete }) => {
  const [openMenu, setOpenMenu] = useState<'generate' | 'streams' | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progressData, setProgressData] = useState({ currentTask: '', currentIndex: 0, totalTasks: 0 });
  const [progressTaskType, setProgressTaskType] = useState<'article' | 'quick_win' | 'image_selection' | 'general'>('quick_win');
  const [selectedStreams, setSelectedStreams] = useState<string[]>([]);
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [calendarStartDate, setCalendarStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [includeArticlesMode, setIncludeArticlesMode] = useState<'all' | 'from_date'>('all');
  const [articlesFromDate, setArticlesFromDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [excludePreviouslyUsed, setExcludePreviouslyUsed] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Confirmation modal state
  const [confirmType, setConfirmType] = useState<string | null>(null);
  const [confirmLabel, setConfirmLabel] = useState('');
  const [confirmTasks, setConfirmTasks] = useState<any[] | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
        setCalendarExpanded(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Open confirmation modal (pre-fetches tasks for live count)
  const requestGenerate = async (type: string, label: string) => {
    if (generating) return;
    setOpenMenu(null);
    setCalendarExpanded(false);
    setConfirmType(type);
    setConfirmLabel(label);
    setConfirmTasks(null); // triggers "Calculating…" in modal
    const tasks = await getTasksForProject(projectId);
    setConfirmTasks(tasks);
  };

  const dismissConfirm = () => {
    setConfirmType(null);
    setConfirmLabel('');
    setConfirmTasks(null);
  };

  const generate = async (type: string, overrideStreams?: string[], preloadedTasks?: any[]) => {
    if (generating) return;
    if (type !== 'content_calendar') setOpenMenu(null);

    setGenerating(true);
    setShowProgress(true);
    setProgressData({ currentTask: 'Loading articles...', currentIndex: 0, totalTasks: 0 });

    const streamsToUse = overrideStreams !== undefined ? overrideStreams : selectedStreams;

    try {
      const tasks = preloadedTasks ?? await getTasksForProject(projectId);
      let articles = tasks.filter((t: any) =>
        t.status === 'completed' &&
        t.synthesized_result &&
        !(t.input_data && typeof t.input_data === 'object' && 'quick_win_type' in t.input_data)
      );
      if (streamsToUse.length > 0) {
        articles = articles.filter((t: any) => {
          const stream = (t.input_data as any)?.masterplan_stream;
          return stream && streamsToUse.includes(stream);
        });
      }
      if (articles.length === 0) {
        alert(streamsToUse.length > 0
          ? 'No completed articles in the selected streams.'
          : 'No completed articles found for this project.');
        return;
      }

      if (type === 'hero_images') {
        const before = articles.length;
        articles = articles.filter((t: any) =>
          !(t.output_data && typeof t.output_data === 'object' && t.output_data.images)
        );
        if (before > 0 && articles.length === 0) {
          alert('All articles already have hero images selected.');
          return;
        }
      } else {
        const qwTypeMap: Record<string, string> = {
          social_media: 'social_media_posts',
          meta_data: 'meta_data',
          ad_copy: 'ad_copy_variations',
          keyword_research: 'keyword_research',
        };
        const backendType = qwTypeMap[type];
        if (backendType) {
          const alreadyHave = new Set(
            tasks
              .filter(t => t.input_data && (t.input_data as any).quick_win_type === backendType)
              .map(t => (t.input_data as any).source_task_id)
              .filter(Boolean)
          );
          const before = articles.length;
          articles = articles.filter(t => !alreadyHave.has(t.task_id));
          if (before > 0 && articles.length === 0) {
            alert(`All articles already have ${type.replace(/_/g, ' ')} generated.`);
            return;
          }
        }
      }

      setProgressData({ currentTask: 'Initializing...', currentIndex: 0, totalTasks: articles.length });
      let label = type.replace(/_/g, ' ');
      let createdCount = 0;

      // Generic helper: keeps the progress meter honest by reflecting in-flight
      // work BEFORE the await and only incrementing the completed count AFTER it
      // resolves. With one item the bar stays at 0% / "Generating: …" until the
      // call returns, then jumps to 100% / "All done!" — never "complete while
      // still running".
      const runWithProgress = async (
        items: any[],
        worker: (item: any, index: number) => Promise<unknown>,
      ) => {
        for (let i = 0; i < items.length; i++) {
          setProgressData({ currentTask: items[i].name, currentIndex: i, totalTasks: items.length });
          await worker(items[i], i);
          setProgressData({ currentTask: items[i].name, currentIndex: i + 1, totalTasks: items.length });
        }
      };

      if (type === 'social_media') {
        label = 'Social Content';
        setProgressTaskType('quick_win');
        // Route through the multichannel repurpose pipeline so the result is
        // registered on the source article (output_data.multichannel) and
        // visible in the MultiChannel module — and so each post gets a hero
        // image fetched per article.
        await runWithProgress(articles, (a) => repurposeArticle({ task_id: a.task_id, project_id: projectId, generate_email_drip: false, auto_fetch_image: true }));
        createdCount = articles.length;
      } else if (type === 'meta_data') {
        label = 'SEO Metadata';
        setProgressTaskType('quick_win');
        await runWithProgress(articles, (a) => createQuickWinMetaData(a.task_id));
        createdCount = articles.length;
      } else if (type === 'ad_copy') {
        label = 'Ad Copy';
        setProgressTaskType('quick_win');
        await runWithProgress(articles, (a) => createQuickWinAdCopy(a.task_id));
        createdCount = articles.length;
      } else if (type === 'keyword_research') {
        label = 'Keyword Research';
        setProgressTaskType('quick_win');
        await runWithProgress(articles, (a) => createQuickWinKeywordResearch(a.task_id));
        createdCount = articles.length;
      } else if (type === 'email_newsletter') {
        label = 'Newsletter';
        setProgressTaskType('quick_win');
        setProgressData({ currentTask: 'Generating newsletter…', currentIndex: 0, totalTasks: 1 });
        await createQuickWinEmailNewsletter(projectId, articles.map(t => t.task_id));
        setProgressData({ currentTask: 'Newsletter', currentIndex: 1, totalTasks: 1 });
        createdCount = 1;
      } else if (type === 'hero_images') {
        label = 'Hero Image';
        setProgressTaskType('image_selection');
        await runWithProgress(articles, (a) => selectImagesForTask(a.task_id));
        createdCount = articles.length;
      } else if (type === 'content_calendar') {
        label = 'Content Calendar';
        setProgressData({ currentTask: 'Creating content calendar task...', currentIndex: 0, totalTasks: 1 });
        // In from-date mode, delegate article selection to the backend
        // (empty task_ids + auto_select): the endpoint's round-robin filter
        // applies articles_from_date server-side. Sending the full visible
        // article list here used to override the date filter entirely —
        // the backend prioritises task_ids over articles_from_date.
        const fromDateMode = includeArticlesMode === 'from_date';
        const newTask = await createQuickWinContentCalendar(
          projectId,
          fromDateMode ? [] : articles.map(t => t.task_id),
          'weekly', calendarStartDate,
          fromDateMode, undefined,
          fromDateMode ? articlesFromDate : undefined,
          excludePreviouslyUsed
        );
        setProgressData({ currentTask: 'Generating calendar...', currentIndex: 0, totalTasks: 1 });
        await new Promise<void>((resolve, reject) => {
          const execToken = localStorage.getItem('access_token') || '';
          const es = new EventSource(`${API_BASE_URL}/tasks/${newTask.task_id}/execute_stream/?token=${encodeURIComponent(execToken)}`);
          let settled = false;
          // 300s matches nginx /api proxy_read_timeout (same rationale as the
          // CRM-import suggest fix) — large calendars exceed the old 120s.
          const timeout = setTimeout(() => {
            es.close();
            if (!settled) { settled = true; reject(new Error('Timed out')); }
          }, 300000);
          es.onmessage = (ev) => {
            const data = JSON.parse(ev.data);
            // Keep currentIndex at 0 while task is still being assembled — only
            // jump to 100% (1/1) when the SSE 'complete' event arrives, so the
            // modal does not claim done while work is still running.
            if (data.status === 'task_complete') setProgressData({ currentTask: data.task_name || 'Generating calendar…', currentIndex: 0, totalTasks: 1 });
            else if (data.status === 'complete') { clearTimeout(timeout); es.close(); createdCount = data.completed; setProgressData({ currentTask: 'Content Calendar', currentIndex: 1, totalTasks: 1 }); if (!settled) { settled = true; resolve(); } }
            else if (data.status === 'error') { clearTimeout(timeout); es.close(); if (!settled) { settled = true; reject(new Error(data.message)); } }
          };
          es.onerror = () => { if (settled) return; clearTimeout(timeout); es.close(); settled = true; reject(new Error('Connection lost')); };
        });
        setOpenMenu(null);
        setCalendarExpanded(false);
      }

      alert(`✓ ${label}${type !== 'content_calendar' && createdCount > 1 ? 's' : ''} generated successfully!`);
      onComplete();
    } catch (err: any) {
      console.error('Generation failed:', err);
      alert('Generation failed. Please try again.');
    } finally {
      setGenerating(false);
      setShowProgress(false);
    }
  };

  return (
    <div className="catalogue-menubar" ref={menuRef}>
      {showProgress && (
        <ProgressModal
          isOpen={showProgress}
          currentTask={progressData.currentTask}
          currentIndex={progressData.currentIndex}
          totalTasks={progressData.totalTasks}
          taskType={progressTaskType}
        />
      )}

      {confirmType && (
        <GenerateConfirmModal
          type={confirmType}
          label={confirmLabel}
          streamNames={streamNames}
          allTasks={confirmTasks}
          initialStreams={selectedStreams}
          onConfirm={(chosenStreams) => {
            setSelectedStreams(chosenStreams);
            const type = confirmType!;
            const tasks = confirmTasks ?? undefined;
            dismissConfirm();
            generate(type, chosenStreams, tasks);
          }}
          onCancel={dismissConfirm}
        />
      )}

      {/* Generate menu */}
      <div className="menubar-item">
        <button
          className={`menubar-btn${openMenu === 'generate' ? ' active' : ''}${generating ? ' generating' : ''}`}
          onClick={() => !generating && setOpenMenu(p => p === 'generate' ? null : 'generate')}
          disabled={generating}
        >
          {generating ? 'Generating…' : 'Generate'}
          <ChevronDown size={11} />
        </button>
        {openMenu === 'generate' && (
          <div className="menubar-dropdown">
            <button className="dropdown-item" onClick={() => requestGenerate('hero_images', 'Hero Images')}>
              <ImageIcon size={13} /> Hero Images
            </button>
            <button className="dropdown-item" onClick={() => requestGenerate('social_media', 'Social Media Posts')}>
              <Share2 size={13} /> Social Media Posts
            </button>
            <button className="dropdown-item" onClick={() => requestGenerate('ad_copy', 'Ad Copy')}>
              <Megaphone size={13} /> Ad Copy
            </button>
            <div className="dropdown-divider" />
            <button className="dropdown-item" onClick={() => requestGenerate('meta_data', 'SEO Metadata')}>
              <Search size={13} /> SEO Metadata
            </button>
            <button className="dropdown-item" onClick={() => requestGenerate('keyword_research', 'Keyword Research')}>
              <Key size={13} /> Keyword Research
            </button>
            <div className="dropdown-divider" />
            <button className="dropdown-item" onClick={() => requestGenerate('email_newsletter', 'Email / Newsletter')}>
              <Mail size={13} /> Email / Newsletter
            </button>
            <button
              className={`dropdown-item calendar-toggle${calendarExpanded ? ' cal-active' : ''}`}
              onClick={() => setCalendarExpanded(p => !p)}
            >
              <CalendarDays size={13} />
              Content Calendar
              <ChevronDown size={11} className={`cal-chevron${calendarExpanded ? ' rotated' : ''}`} />
            </button>
            {calendarExpanded && (
              <div className="calendar-options-panel">
                <label className="cal-label">Publication Date</label>
                <input type="date" className="cal-date-input" value={calendarStartDate}
                  onChange={e => setCalendarStartDate(e.target.value)} />
                <label className="cal-label">Include Articles</label>
                <select className="cal-select" value={includeArticlesMode}
                  onChange={e => setIncludeArticlesMode(e.target.value as 'all' | 'from_date')}>
                  <option value="all">All articles</option>
                  <option value="from_date">From date</option>
                </select>
                {includeArticlesMode === 'from_date' && (
                  <input type="date" className="cal-date-input" value={articlesFromDate}
                    onChange={e => setArticlesFromDate(e.target.value)} style={{ marginTop: '6px' }} />
                )}
                <label className="cal-checkbox-label">
                  <input type="checkbox" checked={excludePreviouslyUsed}
                    onChange={e => setExcludePreviouslyUsed(e.target.checked)} />
                  Exclude previously used articles
                </label>
                <button className="cal-generate-btn" onClick={() => generate('content_calendar')}>
                  Generate Calendar
                </button>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default CatalogueMenuBar;
